<template>
  <!-- Voucher code sheet: what the member shows at the counter. -->
  <button
    style="display: none"
    type="button"
    class="btn-voucher-code-modal"
    data-bs-toggle="modal"
    data-bs-target="#modalVoucherCode"
  ></button>
  <div
    class="modal fade moo-bottom-sheet"
    id="modalVoucherCode"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content vcs">
        <div class="vcs__head">
          <div class="vcs__title">
            <div class="vcs__name">{{ detail.reward_title || "" }}</div>
            <div class="vcs__expiry" v-if="detail.expiry_date_text">
              {{ $t('myVouchers.expiry', { date: detail.expiry_date_text }) }}
            </div>
          </div>
          <button
            type="button"
            class="vcs__close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <span class="material-icons-round">close</span>
          </button>
        </div>

        <div class="vcs__scroll">
          <!-- No QR on the used/expired tab: a code that cannot be redeemed
               should not look scannable. -->
          <div class="vcs__qr" v-if="showQr && detail.code">
            <VueQrcode
              :text="detail.code"
              :size="188"
              color="#241a12"
              bg-color="#ffffff"
              error-level="L"
            />
          </div>

          <div class="vcs__value" v-if="detail.code">
            <span class="vcs__text">{{ detail.code }}</span>
            <button type="button" class="vcs__copy" @click="copyCode()">
              <span class="material-icons-outlined">content_copy</span>
            </button>
          </div>

          <div class="vcs__rules" v-if="applicability.length">
            <div class="vcs__rule" v-for="(line, i) in applicability" :key="i">
              <span class="material-icons-round vcs__check">check_circle</span>
              <span>{{ line }}</span>
            </div>
          </div>

          <template v-if="termsLines.length">
            <div class="vcs__section">{{ $t('rewardCatalogue.termsTitle') }}</div>
            <div class="vcs__rules">
              <div class="vcs__rule" v-for="(line, i) in termsLines" :key="i">
                <span class="material-icons-round vcs__check">check_circle</span>
                <span>{{ line }}</span>
              </div>
            </div>
          </template>

          <template v-if="detail.where_to_use">
            <div class="vcs__section">{{ $t('rewardCatalogue.whereToUse') }}</div>
            <div class="vcs__rules">
              <div class="vcs__rule">
                <span class="material-icons-round vcs__check">place</span>
                <span class="vcs__where" v-html="detail.where_to_use"></span>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import "bootstrap/dist/js/bootstrap.bundle";
import VueQrcode from "vue-qrcode-component";

/**
 * The sheet a member shows at the counter: QR, the code itself, and the rules
 * attached to the voucher.
 *
 * Opened by clicking `.btn-voucher-code-modal`, the same hidden-trigger pattern
 * ModalSuccess and ModalConfirmSheet use, so a caller only has to fill `detail`
 * and click. `detail` is whatever /member/rewards/detail/{id}/1 returned — the
 * copy issued to THIS member, so `code` is theirs.
 */
export default {
  name: "VoucherCodeSheet",
  components: { VueQrcode },
  // Declared so Vue does not treat them as native listeners: this component's
  // root is a fragment, where fallthrough attributes have nowhere to land.
  emits: ["copied", "copy-failed"],
  props: {
    detail: {
      type: Object,
      default: () => ({}),
    },
    // Off for used, expired and locked vouchers: a code that cannot be redeemed
    // should not be presented as scannable.
    showQr: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    // The four "this voucher applies to ..." lines, same set RewardDetail shows.
    applicability() {
      return [
        this.detail.tier_can_exchange_text,
        this.detail.branch_can_exchange_text,
        this.detail.item_apply_text,
        this.detail.order_minimum_text,
      ].filter((line) => line);
    },
    termsLines() {
      return (this.detail.terms_arr ?? []).filter((line) => line);
    },
  },
  methods: {
    copyCode() {
      const code = this.detail.code ?? "";
      if (!code) {
        return;
      }

      const done = () => this.$emit("copied", code);

      // navigator.clipboard needs a secure context and is missing from some
      // in-app browsers, hence the textarea fallback.
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(code).then(done, () => this.copyFallback(code, done));
        return;
      }
      this.copyFallback(code, done);
    },
    copyFallback(code, done) {
      const area = document.createElement("textarea");
      area.value = code;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
        done();
      } catch (e) {
        this.$emit("copy-failed", code);
      }
      document.body.removeChild(area);
    },
  },
};
</script>

<style scoped>
/* Slide-in and positioning come from .moo-bottom-sheet in moo-sheet.css.
   Tokens (--sheet-*) come from the .moo-sheet-page ancestor. */
.vcs {
  border: 0;
  border-radius: 24px 24px 0 0;
  padding: 0 20px calc(20px + env(safe-area-inset-bottom, 0px));
  box-shadow: 0 -12px 30px rgba(25, 28, 30, 0.12);
}
.vcs::before {
  content: "";
  display: block;
  width: 36px;
  height: 4px;
  margin: 8px auto 0;
  border-radius: 999px;
  background: var(--sheet-border);
}
.vcs__head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 0 12px;
}
.vcs__title {
  flex: 1;
  min-width: 0;
}
.vcs__name {
  font-size: 16px;
  font-weight: 800;
  color: #241a12;
  overflow-wrap: anywhere;
}
.vcs__expiry {
  margin-top: 2px;
  font-size: 12px;
  color: #857372;
}
.vcs__close {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: var(--sheet-inset);
  color: #6b6868;
  cursor: pointer;
}
.vcs__close .material-icons-round {
  font-size: 18px;
}
.vcs__scroll {
  /* The terms of a voucher run to any length; the sheet stays put and the
     content scrolls inside it. */
  max-height: 62vh;
  overflow-y: auto;
}
.vcs__qr {
  display: flex;
  justify-content: center;
  padding: 14px;
  background: #ffffff;
  border: 1px solid var(--sheet-border);
  border-radius: 16px;
}
.vcs__value {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  background: var(--sheet-bg);
  border: 1px solid var(--sheet-border);
  border-radius: 12px;
  padding: 10px 12px;
}
.vcs__text {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #241a12;
  overflow-wrap: anywhere;
}
.vcs__copy {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 9999px;
  background: var(--sheet-12);
  color: var(--sheet);
  cursor: pointer;
}
.vcs__copy .material-icons-outlined {
  font-size: 18px;
}
.vcs__section {
  margin: 18px 0 8px;
  font-size: 13px;
  font-weight: 800;
  color: #241a12;
}
.vcs__rules {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 14px;
}
.vcs__rule {
  display: flex;
  gap: 8px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #4a4342;
}
.vcs__check {
  font-size: 18px;
  line-height: 1.5;
  color: var(--sheet);
  flex-shrink: 0;
}
/* where_to_use is merchant-authored HTML, out of scoped-style reach. */
.vcs__where :deep(p) {
  margin: 0;
}
.vcs__where :deep(a) {
  color: var(--sheet);
  font-weight: 600;
  text-decoration: underline;
}
</style>
